import type {ReliefReason} from './videoReliefAdvisor';

export interface StatEntry {
    readonly id: string;
    readonly type: string;
    readonly kind?: string;
    readonly mediaType?: string;
    readonly [key: string]: unknown;
}

export const LOSS_LIMIT = 0.08;
export const RTT_LIMIT_S = 0.3;
export const RTT_HARD_LIMIT_S = 0.5;
export const RTT_LOSS_LIMIT = 0.03;
export const DROP_LIMIT = 0.2;
const MIN_PACKETS = 20;
const MIN_FRAMES = 10;

export class StatsDeltaTracker {
    private readonly previous = new Map<string, Readonly<Record<string, number>>>();
    private readonly seen = new Set<string>();

    delta(id: string, counters: Readonly<Record<string, number>>): Readonly<Record<string, number>> | undefined {
        this.seen.add(id);
        const before = this.previous.get(id);
        this.previous.set(id, counters);
        if (!before) {
            return undefined;
        }
        const out: Record<string, number> = {};
        for (const key of Object.keys(counters)) {
            const change = counters[key] - (before[key] ?? 0);
            if (change < 0) {
                return undefined;
            }
            out[key] = change;
        }
        return out;
    }

    endRound(): void {
        for (const id of [...this.previous.keys()]) {
            if (!this.seen.has(id)) {
                this.previous.delete(id);
            }
        }
        this.seen.clear();
    }

    clear(): void {
        this.previous.clear();
        this.seen.clear();
    }
}

export interface StatsVerdict {
    readonly degraded: ReliefReason | null;
    readonly known: boolean;
}

const kindOf = (stat: StatEntry): string | undefined => stat.kind ?? stat.mediaType;

const num = (value: unknown): number | undefined => (typeof value === 'number' && Number.isFinite(value) ? value : undefined);

export const analyzeStats = (entries: readonly StatEntry[], tracker: StatsDeltaTracker, paused: boolean): StatsVerdict => {
    let network = false;
    let cpu = false;
    let known = false;
    for (const stat of entries) {
        const kind = kindOf(stat);
        if (stat.type === 'outbound-rtp' && kind === 'video' && !paused) {
            known = true;
            if (stat.qualityLimitationReason === 'cpu') {
                cpu = true;
            } else if (stat.qualityLimitationReason === 'bandwidth') {
                network = true;
            }
        } else if (stat.type === 'remote-inbound-rtp' && (!paused || kind === 'audio')) {
            const loss = num(stat.fractionLost);
            const rtt = num(stat.roundTripTime);
            known = known || loss !== undefined || rtt !== undefined;
            const slow = rtt !== undefined && (rtt > RTT_HARD_LIMIT_S || (rtt > RTT_LIMIT_S && (loss ?? 0) > RTT_LOSS_LIMIT));
            if ((loss !== undefined && loss > LOSS_LIMIT) || slow) {
                network = true;
            }
        } else if (stat.type === 'inbound-rtp' && kind === 'audio') {
            const received = num(stat.packetsReceived);
            const lost = num(stat.packetsLost);
            if (received !== undefined && lost !== undefined) {
                const change = tracker.delta(stat.id, {received, lost: Math.max(0, lost)});
                if (change) {
                    known = true;
                    const total = change.received + change.lost;
                    if (total >= MIN_PACKETS && change.lost / total > LOSS_LIMIT) {
                        network = true;
                    }
                }
            }
        } else if (stat.type === 'inbound-rtp' && kind === 'video' && !paused) {
            const decoded = num(stat.framesDecoded);
            const dropped = num(stat.framesDropped);
            if (decoded !== undefined && dropped !== undefined) {
                const change = tracker.delta(stat.id, {decoded, dropped});
                if (change) {
                    known = true;
                    const total = change.decoded + change.dropped;
                    if (total >= MIN_FRAMES && change.dropped / total > DROP_LIMIT) {
                        cpu = true;
                    }
                }
            }
        }
    }
    tracker.endRound();
    return {degraded: network ? 'network' : cpu ? 'cpu' : null, known};
};

export const statsEntries = (report: {forEach: (callback: (value: unknown) => void) => void}): readonly StatEntry[] => {
    const out: StatEntry[] = [];
    report.forEach(value => {
        if (value && typeof value === 'object' && typeof (value as StatEntry).id === 'string' && typeof (value as StatEntry).type === 'string') {
            out.push(value as StatEntry);
        }
    });
    return out;
};
